// react
import { useMemo } from 'react';
import styled from 'styled-components';

// models
import { FieldType } from '@karehero/models';

// components
import { Editor } from '@/components/organisms/Editor/Editor';
import { createFieldHelper } from '@/components/organisms/EditorFields/fieldHelper';

export interface Address {
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  city?: string;
  postalCode?: string;
}

/**
 * AddressField allows you to add an address field to your form.
 */
export const AddressField = ({
  label,
  value,
  onChange,
  isDisabled,
  isRequired,
}: AddressFieldProps) => {
  // memo
  const contact = useMemo(() => {
    let c: Partial<Address> = {};
    try {
      c = JSON.parse(value || '{}');
    } catch (e) {
      console.error(e);
    }
    return c;
  }, [value]);

  const fields = useMemo(
    () =>
      createFieldHelper<Address>({
        addressLine1: {
          label: 'Address Line 1',
          isRequired: isRequired,
          field: {
            type: FieldType.Text,
          },
        },
        addressLine2: {
          label: 'Address Line 2',
          field: {
            type: FieldType.Text,
          },
        },
        addressLine3: {
          label: 'Address Line 3',
          field: {
            type: FieldType.Text,
          },
        },
        city: {
          label: 'City',
          isRequired: isRequired,
          field: {
            type: FieldType.Text,
          },
        },
        postalCode: {
          label: 'Postal Code',
          isRequired: isRequired,
          field: {
            type: FieldType.Text,
          },
        },
      }),
    [],
  );

  return (
    <Wrapper>
      <Title>{label}</Title>
      <Editor
        fields={fields}
        value={contact}
        onChange={(val) => onChange(JSON.stringify(val))}
        isFloatingActions={false}
        isActionsDisabled={true}
        isForm={false}
        isDisabled={isDisabled}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.div`
  font: ${(props) => props.theme.typography.body.lg.regular};
  color: ${(props) => props.theme.color.primary[90]};
`;

interface AddressFieldProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
}

AddressField.displayName = 'AddressField';

export default AddressField;
